import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { formatCompactCurrency } from '../../../../utils/currencyFormatter';
import type { CompanyState } from '../../../../types/company';

interface FinanceSectionProps {
  companyState: CompanyState;
  onNavigate?: () => void;
}

export function FinanceSection({ companyState, onNavigate }: FinanceSectionProps) {
  if (!companyState) {
    return (
      <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
        <button
          onClick={onNavigate}
          className={`absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 text-center font-bold text-sm text-theme-text-secondary border-b border-theme-border ${
            onNavigate ? 'hover:bg-theme-bg-tertiary hover:text-theme-accent transition-colors cursor-pointer' : ''
          }`}
          disabled={!onNavigate}
        >
          Finance
        </button>
        <div className="pt-10 p-3 h-full text-center text-theme-text-muted">
          Loading...
        </div>
      </div>
    );
  }

  // Calculate monthly metrics
  const monthlyRevenue = companyState.dailyStats?.revenue ? companyState.dailyStats.revenue * 30 : 0;
  const monthlyCosts = companyState.dailyStats?.costs ? companyState.dailyStats.costs * 30 : 0;
  const monthlyProfit = companyState.dailyStats?.profit ? companyState.dailyStats.profit * 30 : 0;

  const metrics = [
    {
      label: 'Monthly Revenue',
      value: monthlyRevenue,
      icon: DollarSign,
      color: 'text-theme-info',
      bgColor: 'bg-theme-info-light'
    },
    {
      label: 'Monthly Costs',
      value: monthlyCosts,
      icon: TrendingDown,
      color: 'text-theme-error',
      bgColor: 'bg-theme-error-light'
    },
    {
      label: 'Net Profit',
      value: monthlyProfit,
      icon: monthlyProfit >= 0 ? TrendingUp : TrendingDown,
      color: monthlyProfit >= 0 ? 'text-theme-success' : 'text-theme-error',
      bgColor: monthlyProfit >= 0 ? 'bg-theme-success-light' : 'bg-theme-error-light'
    }
  ];

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
      <button
        onClick={onNavigate}
        className={`absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 text-center font-bold text-sm text-theme-text-secondary border-b border-theme-border ${
          onNavigate ? 'hover:bg-theme-bg-tertiary hover:text-theme-accent transition-colors cursor-pointer' : ''
        }`}
        disabled={!onNavigate}
      >
        Finance
      </button>

      <div className="pt-10 p-3 h-full overflow-y-auto scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        <div className="space-y-2">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className={`${metric.bgColor} rounded-lg p-2`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-3 h-3 ${metric.color}`} />
                    <span className="text-xs font-medium text-theme-text-secondary">
                      {metric.label}
                    </span>
                  </div>
                  <span className={`font-bold text-xs ${metric.color}`}>
                    {formatCompactCurrency(metric.value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Additional Info */}
        <div className="mt-3 pt-2 border-t border-theme-border">
          <div className="text-xs text-theme-text-secondary text-center">
            Current Cash: {formatCompactCurrency(companyState.cash)}
          </div>
        </div>
      </div>
    </div>
  );
}